import { useLocalStorage } from '@mantine/hooks'
import { useCallback } from 'react'
import { AiBridgeTestStatus } from './useAiBridgeTest'

const STORAGE_KEY = 'ai-bridge-last-operation'

export type AiBridgeOperationType = 'test' | 'restart'

// `status` reuses the test vocabulary so both operations render through the same
// colour map; a restart only ever reports 'passed' or 'failed'.
export type AiBridgeOperation = {
  type: AiBridgeOperationType
  status: AiBridgeTestStatus
  message: string
  at: string
}

// Neither operation leaves a trace on the platform: restart is a one-way RPC and the
// AIBridge model stores nothing about either. Keeping the last outcome per bridge here
// is the only record there is, and it lives in the operator's own browser.
export const useAiBridgeOperationLog = () => {
  const [operationLog, setOperationLog] = useLocalStorage<
    Record<string, AiBridgeOperation>
  >({
    key: STORAGE_KEY,
    defaultValue: {}
  })

  const recordOperation = useCallback(
    (
      bridgeId: string,
      operation: Omit<AiBridgeOperation, 'at'> & { at?: string }
    ) =>
      setOperationLog((current) => ({
        ...current,
        [bridgeId]: {
          ...operation,
          at: operation.at ?? new Date().toISOString()
        }
      })),
    [setOperationLog]
  )

  return { operationLog, recordOperation }
}
